import '@xyflow/react/dist/style.css'

import React, { useCallback, useRef, useState } from 'react'
import {
  addEdge,
  Background,
  BackgroundVariant,
  Connection,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react'
import { v4 as uuid4 } from 'uuid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table.js'
import { IActionRepeaterValue, IActionSettings, IActionStep, IActionType } from '@makyoapp/makyo-common'
import { Redo16, Undo16 } from '@makyoapp/makyo-icon/icon'
import { AddActionVerticalNodeModal, Button, ButtonVariant } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { isActionHasSettings } from '@/context/ActionFormProviderV2'
import { useUndoRedoContext } from '@/context/UndoRedoContext'
import { ComponentSettingKey } from '@/shared/builder'
import { SHORTCUT_PRESETS, useKeyboardShortcuts } from '@/shared/hooks'
import { getTableColumnsWithRowId } from '@/shared/utils'
import { SaveButton } from '@/components/SaveButton'

import { SwitchCase } from '../action-forms/conditional/types'
import { AutoLayoutControls } from './AutoLayoutControls'
import { LAYOUT, NODE_STYLES, REACT_FLOW_CONFIG, SPECIAL_NODES } from './constants'
import { WorkflowProvider } from './context/WorkflowContext'
import { useAutoLayout } from './hooks/useAutoLayout'
import { WorkflowEdgeManager, WorkflowManager, WorkflowNodeManager } from './managers'
import { AdvancedWorkflowEditorProps, WorkflowEdge, WorkflowFormData, WorkflowNode, WorkflowNodeData } from './types'
import { WorkflowActionFormModal } from './WorkflowActionFormModal'
import { WorkflowEdgeComponent } from './WorkflowEdgeComponent'
import { WorkflowNodeComponent } from './WorkflowNodeComponent'

// Utility function to transform old array-based structure to new nextAction structure
const transformToNewStructure = (nodes: WorkflowNode[], edges: WorkflowEdge[]): IActionRepeaterValue[] => {
  const nodeMap = new Map<string, WorkflowNode>()
  const edgeMap = new Map<string, WorkflowEdge[]>()

  // Create maps for easy lookup
  nodes.forEach((node) => {
    nodeMap.set(node.id, node)
  })

  edges.forEach((edge) => {
    if (!edgeMap.has(edge.source)) {
      edgeMap.set(edge.source, [])
    }
    edgeMap.get(edge.source)!.push(edge)
  })

  const result: IActionRepeaterValue[] = []

  // Helper function to get next action for a node
  const getNextAction = (nodeId: string): string | IActionStep[] | null => {
    const outgoingEdges = edgeMap.get(nodeId) || []

    if (outgoingEdges.length === 0) {
      return null
    }

    const node = nodeMap.get(nodeId)
    if (!node) return null

    const actionSteps: IActionStep[] = []

    switch (node.data.actionValue.type) {
      case IActionType.ConditionalIf:
        const trueEdge = outgoingEdges.find(
          (edge) => edge.sourceHandle === WorkflowNodeManager.getSourceHandle(nodeId, 'true')
        )
        const falseEdge = outgoingEdges.find(
          (edge) => edge.sourceHandle === WorkflowNodeManager.getSourceHandle(nodeId, 'false')
        )

        if (trueEdge) {
          actionSteps.push({
            actionId: trueEdge.target,
            default: false, // true branch
            type: node.data.actionValue.type,
            condition: node.data.actionValue.settings?.conditions,
          })
        }

        if (falseEdge) {
          actionSteps.push({
            actionId: falseEdge.target,
            default: true, // false branch
            type: node.data.actionValue.type,
            condition: null,
          })
        }
        break
      case IActionType.ConditionalSwitch:
        // Handle ConditionalSwitch with multiple cases
        const settings = node.data.actionValue.settings || {}
        const cases = settings.cases || []

        cases.forEach((caseItem: any) => {
          const caseEdge = outgoingEdges.find(
            (edge) => edge.sourceHandle === WorkflowNodeManager.getSourceHandle(nodeId, caseItem.id)
          )
          if (caseEdge) {
            actionSteps.push({
              actionId: caseEdge.target,
              type: node.data.actionValue.type,
              condition: caseItem.condition,
              default: false,
            })
          }
        })
        break

      case IActionType.AddToCart:
        const insideCartEdge = outgoingEdges.find(
          (edge) => edge.sourceHandle === WorkflowNodeManager.getSourceHandle(nodeId, 'insideCart')
        )
        const outsideCartEdge = outgoingEdges.find(
          (edge) => edge.sourceHandle === WorkflowNodeManager.getSourceHandle(nodeId, 'outsideCart')
        )

        if (insideCartEdge) {
          actionSteps.push({
            actionId: insideCartEdge.target,
            type: node.data.actionValue.type,
            condition: null,
            default: false,
          })
        }

        if (outsideCartEdge) {
          actionSteps.push({
            actionId: outsideCartEdge.target,
            type: node.data.actionValue.type,
            condition: null,
            default: true,
          })
        }
        break

      default:
        const nextEdge = outgoingEdges[0] // Take the first edge for non-conditional
        return nextEdge ? nextEdge.target : null
    }

    return actionSteps.length > 0 ? actionSteps : null
  }

  // Process each node
  nodes.forEach((node) => {
    if (node.data.actionValue.id === SPECIAL_NODES.trigger) {
      return // Skip trigger node
    }

    const nextAction = getNextAction(node.id) ?? []
    // For conditional actions, remove conditions from settings since they're now in nextAction
    let cleanSettings = { ...node.data.actionValue.settings }

    result.push({
      id: node.id,
      type: node.data.actionValue.type,
      settings: cleanSettings,
      nextAction,
      label: WorkflowNodeManager.getNodeLabel(node.data),
    })
  })

  return result
}

// Utility function to transform new nextAction structure back to old array-based structure
const transformToOldStructure = (actions: IActionRepeaterValue[]): IActionRepeaterValue[] => {
  return actions.map((action, index) => ({
    id: action.id,
    type: action.type,
    settings: action.settings,
    nextAction: index < actions.length - 1 ? actions[index + 1].id : undefined,
    label: action.label,
  }))
}

// Function to initialize from old structure (array-based) to new structure
const initializeFromOldStructure = (oldNodes: IActionRepeaterValue[]): IActionRepeaterValue[] => {
  // If the nodes already have nextAction property, they're already in new format
  const hasNewStructure = oldNodes.some((node) => node.nextAction !== undefined)

  if (hasNewStructure) {
    return oldNodes
  }

  // Convert old array-based structure to new structure
  const newStructureActions = transformToOldStructure(oldNodes)
  return newStructureActions
}

const nodeTypes = {
  workflowNode: WorkflowNodeComponent,
}

const edgeTypes = {
  workflowEdge: WorkflowEdgeComponent,
}

const WorkflowEditorContent: React.FC<AdvancedWorkflowEditorProps> = ({
  nodes: workflowNodes,
  onClose,
  onSave,
  triggerLabel,
  customPluginActions,
  excludeActions = [],
}) => {
  const { canUndo, canRedo, handleUndo, handleRedo } = useUndoRedoContext()
  const { activeComponent, activeLayerClient } = useActiveLayerContext()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [sourceNodeId, setSourceNodeId] = useState<string | null>(null)
  const [editedForm, setEditedForm] = useState<WorkflowFormData | null>(null)
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [insertBetweenInfo, setInsertBetweenInfo] = useState<{
    edgeId: string
    targetNodeId: string
    edgeInfo?: { sourceX: number; sourceY: number; targetX: number; targetY: number }
    sourceBranch?: string | null
  } | null>(null)
  const [branchInfo, setBranchInfo] = useState<string | null>(null)

  // Auto-layout hook
  const { applyConditionalAwareLayout } = useAutoLayout()

  // Saving indicator state
  const [showSavingIndicator, setShowSavingIndicator] = useState(false)
  const savingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Function to show saving indicator with timeout management
  const showSavingIndicatorWithTimeout = useCallback(() => {
    setShowSavingIndicator(true)

    // Clear existing timeout if there is one
    if (savingTimeoutRef.current) {
      clearTimeout(savingTimeoutRef.current)
    }

    // Set new timeout to hide indicator after 2 seconds
    savingTimeoutRef.current = setTimeout(() => {
      setShowSavingIndicator(false)
      savingTimeoutRef.current = null
    }, 500)
  }, [])

  // Initialize with proper structure
  const initializedNodes = initializeFromOldStructure(workflowNodes)

  // Always add a trigger node at the beginning
  const triggerNode: IActionRepeaterValue = {
    id: SPECIAL_NODES.trigger,
    type: triggerLabel as IActionType,
    label: triggerLabel,
    settings: {},
  }

  // Trigger always first
  const allWorkflowNodes = [triggerNode, ...initializedNodes]

  // Convert workflow nodes to React Flow nodes
  const initialNodes: WorkflowNode[] = allWorkflowNodes.map((node, index) => {
    return {
      id: node.id,
      type: 'workflowNode',
      position: { x: index * LAYOUT.nodeSpacing, y: LAYOUT.nodeYPosition },
      data: {
        actionValue: node,
      },
    }
  })

  // Create edges based on the actual nextAction relationships
  const createInitialEdges = (nodes: IActionRepeaterValue[]): WorkflowEdge[] => {
    // Convert WorkflowNode[] to Node[] for the manager
    const reactFlowNodes: WorkflowNode[] = nodes.map((node, index) => ({
      id: node.id,
      type: 'workflowNode',
      position: { x: index * LAYOUT.nodeSpacing, y: LAYOUT.nodeYPosition },
      data: {
        actionValue: node,
      },
      label: node.label,
    }))

    return WorkflowManager.createInitialEdges(reactFlowNodes)
  }

  const initialEdges: WorkflowEdge[] = createInitialEdges(allWorkflowNodes)

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
  const { getEdges, fitView, zoomIn, zoomOut } = useReactFlow()

  const { tables } = useDbClientContext()

  // Track if we should auto-save
  const [shouldAutoSave, setShouldAutoSave] = useState(false)

  // Keyboard shortcuts for zoom
  useKeyboardShortcuts([SHORTCUT_PRESETS.zoomIn(zoomIn), SHORTCUT_PRESETS.zoomOut(zoomOut)])

  // Function to collect all connected nodes from trigger node and transform to new structure
  const handleAutoSaveWorkflow = useCallback(() => {
    const triggerNodeId = SPECIAL_NODES.trigger
    const connectedNodes: WorkflowNode[] = []
    const visited = new Set<string>()

    // Recursive function to traverse connected nodes
    const traverseConnectedNodes = (nodeId: string) => {
      if (visited.has(nodeId)) return
      visited.add(nodeId)

      // Find the node data
      const node = nodes.find((n) => n.id === nodeId)
      if (!node) return

      // Skip trigger node from the result
      if (nodeId !== triggerNodeId) {
        connectedNodes.push({
          id: node.id,
          type: 'workflowNode',
          position: node.position,
          data: {
            actionValue: node.data.actionValue,
          },
        })
      }

      // Find all edges that start from this node
      const outgoingEdges = edges.filter((edge) => edge.source === nodeId)

      // Recursively traverse connected nodes
      outgoingEdges.forEach((edge) => {
        traverseConnectedNodes(edge.target)
      })
    }

    // Start traversal from trigger node
    traverseConnectedNodes(triggerNodeId)

    // Transform to new structure with nextAction
    const newStructureActions = transformToNewStructure(connectedNodes, edges)

    // Show saving indicator
    showSavingIndicatorWithTimeout()

    // Call the onSave callback if provided
    if (onSave) {
      onSave(newStructureActions)
    }
  }, [nodes, edges, onSave, showSavingIndicatorWithTimeout])

  // todo -> currently just showing saving indicator, but not saving the workflow
  const handleSaveWorkflow = useCallback(() => {
    setShowSavingIndicator(true)

    // Clear existing timeout if there is one
    if (savingTimeoutRef.current) {
      clearTimeout(savingTimeoutRef.current)
    }

    // Set new timeout to hide indicator after 2 seconds
    savingTimeoutRef.current = setTimeout(() => {
      setShowSavingIndicator(false)
      savingTimeoutRef.current = null
    }, 500)
  }, [])

  // Auto-save effect
  React.useEffect(() => {
    if (shouldAutoSave) {
      handleAutoSaveWorkflow()
      setShouldAutoSave(false)
    }
  }, [shouldAutoSave, handleAutoSaveWorkflow])

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current)
      }
    }
  }, [])

  // Auto update nodes when workflowNodes changes
  React.useEffect(() => {
    // skip if users detaching the nodes
    if (nodes.length - 1 > workflowNodes.length) {
      return
    }

    setNodes(initialNodes)
    setEdges(initialEdges)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowNodes])

  // Validation function to check if a connection is valid
  const isValidConnection = useCallback(
    (connection: Connection | WorkflowEdge) => {
      const targetNodeId = connection.target

      if (!targetNodeId) {
        return false
      }

      return WorkflowManager.isValidConnection(getEdges(), targetNodeId)
    },
    [getEdges]
  )

  const onConnect = useCallback(
    (params: Connection) => {
      const currentNode = nodes.find((n) => n.id === params.source) as unknown as { data: WorkflowNodeData }
      if (currentNode?.data.actionValue.type === IActionType.ConditionalSwitch) {
        const sourceId = WorkflowNodeManager.extractBranchFromSourceHandle(params.sourceHandle || '') ?? ''
        const newNodes = WorkflowManager.addActionIdToSwitchActionCase(nodes, params.source, sourceId, params.target)
        setNodes(newNodes)
      }

      setEdges((eds) => {
        const newEdges = addEdge(
          {
            ...params,
            type: 'workflowEdge',
            style: NODE_STYLES.edge.default,
          },
          eds
        )

        // Auto-save when a connection is made
        setShouldAutoSave(true)

        return newEdges
      })
    },
    [nodes, setEdges, setNodes]
  )

  const handleOnSave = useCallback(
    (id: string, action: IActionType, data?: IActionSettings, meta?: Record<string, any>) => {
      // Case 1: Creating a new node (editedForm has sourceNodeId)
      if (editedForm?.sourceNodeId) {
        const sourceNode = nodes.find((n) => n.id === editedForm.sourceNodeId!)
        if (!sourceNode) return

        // Create new node using WorkflowManager
        const newNode = WorkflowNodeManager.createNode(
          id,
          action,
          data || {},
          WorkflowNodeManager.getNewNodePosition(sourceNode)
        )

        // Handle insert between case
        if (insertBetweenInfo) {
          const operation = WorkflowManager.insertNodeBetween(nodes, edges, newNode, insertBetweenInfo, action)

          setEdges((prev) => {
            const newEdges = [...prev.filter((edge) => edge.id !== insertBetweenInfo.edgeId), ...operation.newEdges]

            // Auto-save when a node is edited
            setShouldAutoSave(true)

            return newEdges
          })
        } else {
          // Create edge from source to new node
          const isConditional = WorkflowNodeManager.isConditionalNode(sourceNode)
          const sourceHandle =
            isConditional && editedForm.branchInfo !== null && editedForm.branchInfo !== undefined
              ? WorkflowNodeManager.getSourceHandle(editedForm.sourceNodeId, editedForm.branchInfo)
              : undefined

          const newEdge = WorkflowEdgeManager.createEdge(editedForm.sourceNodeId, id, sourceHandle)

          setEdges((prev) => {
            const newEdges = [...prev, newEdge]

            // Auto-save when a node is edited
            setShouldAutoSave(true)

            return newEdges
          })
        }

        if ((sourceNode.data.actionValue as IActionRepeaterValue).type === IActionType.ConditionalSwitch) {
          const updatedNodes = WorkflowManager.addActionIdToSwitchActionCase(
            nodes,
            sourceNode.id,
            editedForm.branchInfo || '',
            id
          )
          setNodes([...updatedNodes, newNode])
        } else {
          setNodes([...nodes, newNode])
        }

        // Clear all states
        setSourceNodeId(null)
        setInsertBetweenInfo(null)
        setBranchInfo(null)

        if (!meta?.preventClose) {
          setIsModalOpen(false)
          setIsFormModalOpen(false)
          setEditedForm(null)
        }
      }
      // Case 2: Updating an existing node (editedForm has id but no sourceNodeId)
      else if (editedForm?.id) {
        const updatedNodes = WorkflowManager.updateNode(nodes, id, action, data || {})

        // We need to remove any edges that are not connected to the new node
        if (editedForm.type === IActionType.ConditionalSwitch) {
          const branchToRemove =
            data?.cases?.map((caseItem: SwitchCase) => WorkflowNodeManager.getSourceHandle(id, caseItem.id)) || []
          if (branchToRemove.length > 0) {
            const newEdges = WorkflowManager.removeEdgesByWhiteListBranchIds(edges, id, branchToRemove)
            setEdges(newEdges)
          }
        }

        setNodes(updatedNodes)

        // Auto-save when a node is edited
        setShouldAutoSave(true)

        if (!meta?.preventClose) {
          setIsFormModalOpen(false)
          setEditedForm(null)
        }
      }

      // Apply auto-layout after any changes
      setTimeout(() => {
        applyConditionalAwareLayout()
      }, 100)
    },
    [editedForm, nodes, edges, insertBetweenInfo, setNodes, setEdges, applyConditionalAwareLayout, setShouldAutoSave]
  )

  const handleAddNode = useCallback(
    (value: { action: IActionType; settings?: any }) => {
      if (!sourceNodeId) return

      const sourceNode = nodes.find((n) => n.id === sourceNodeId)
      if (!sourceNode) return

      const isPluginAction = customPluginActions?.find((pa) => pa.type === value.action)

      // for Form Add Item action, we need to grab the tableId dataSource

      if (isPluginAction) {
        setEditedForm({
          id: uuid4(),
          type: value.action,
          settings:
            value.action === IActionType.None
              ? {}
              : {
                  ...(value.settings || {}),
                  pluginAction: isPluginAction.settings,
                },
          sourceNodeId: sourceNodeId,
          branchInfo: branchInfo,
        })
        setIsFormModalOpen(true)
        return
      }

      // Case 1: Action has settings - open form modal
      if (value.action && isActionHasSettings(value.action)) {
        const newNodeId = uuid4()
        setEditedForm({
          id: newNodeId,
          type: value.action,
          settings: value.settings || {},
          sourceNodeId: sourceNodeId,
          branchInfo: branchInfo,
        })
        setIsFormModalOpen(true)
        return
      }

      // Case 2: Action has no settings - create node immediately
      const operation = WorkflowManager.addNode(
        nodes,
        edges,
        value.action,
        value.settings || {},
        sourceNodeId,
        branchInfo
      )

      // Handle insert between case
      if (insertBetweenInfo) {
        const insertOperation = WorkflowManager.insertNodeBetween(
          nodes,
          edges,
          operation.newNode,
          insertBetweenInfo,
          value.action
        )

        setEdges((prev) => {
          const newEdges = [...prev.filter((edge) => edge.id !== insertBetweenInfo.edgeId), ...insertOperation.newEdges]

          // Auto-save when a new node is added
          setShouldAutoSave(true)

          return newEdges
        })
      } else {
        setEdges((prev) => {
          const newEdges = [...prev, ...operation.newEdges]

          // Auto-save when a new node is added
          setShouldAutoSave(true)

          return newEdges
        })
      }

      setNodes((prev) => [...prev, operation.newNode])

      // Clear all states
      setIsModalOpen(false)
      setSourceNodeId(null)
      setInsertBetweenInfo(null)
      setBranchInfo(null)

      // Apply auto-layout
      setTimeout(() => {
        applyConditionalAwareLayout()
      }, 100)
    },
    [
      sourceNodeId,
      nodes,
      customPluginActions,
      edges,
      branchInfo,
      insertBetweenInfo,
      setNodes,
      setEdges,
      applyConditionalAwareLayout,
    ]
  )

  // Listen for NodePalette open events
  React.useEffect(() => {
    const handleOpenPalette = (event: CustomEvent) => {
      const { sourceNodeId, insertBetween, edgeId, edgeInfo, branch } = event.detail

      setSourceNodeId(sourceNodeId)
      setBranchInfo(branch !== undefined ? branch : null)
      setIsModalOpen(true)

      // Handle insert between case
      if (insertBetween && edgeInfo && edgeId) {
        // Find the edge that was clicked using the edgeId
        const edge = edges.find((e) => e.id === edgeId)
        if (edge) {
          setInsertBetweenInfo({
            edgeId: edge.id,
            targetNodeId: edge.target,
            edgeInfo,
            sourceBranch: branch,
          })
        }
      } else {
        setInsertBetweenInfo(null)
      }
    }

    const handleOpenNodeEdit = (event: CustomEvent) => {
      const { nodeId, actionValue } = event.detail

      // Set the edited form with the existing node data
      setEditedForm({
        id: nodeId,
        type: actionValue.type,
        settings: actionValue.settings || {},
      })
      setIsFormModalOpen(true)
    }

    const handleTriggerAutoSave = () => {
      setShouldAutoSave(true)
    }

    window.addEventListener('openNodePalette', handleOpenPalette as EventListener)
    window.addEventListener('openNodeEdit', handleOpenNodeEdit as EventListener)
    window.addEventListener('triggerAutoSave', handleTriggerAutoSave as EventListener)

    return () => {
      window.removeEventListener('openNodePalette', handleOpenPalette as EventListener)
      window.removeEventListener('openNodeEdit', handleOpenNodeEdit as EventListener)
      window.removeEventListener('triggerAutoSave', handleTriggerAutoSave as EventListener)
    }
  }, [edges])

  // TODO (ACTION-WORKFLOWS): REFACTOR THIS
  // Refactor how to construct action option for a actions that has output
  const getNodeOptions = useCallback(() => {
    const NODES_HAS_OUTPUT = [IActionType.TemporaryVariable, IActionType.FilterData, IActionType.FormAddRow]
    const FILTERED_NODES = nodes.filter(
      (node) => NODES_HAS_OUTPUT.includes(node.data.actionValue.type) && node.id !== editedForm?.id
    )
    return FILTERED_NODES.map((node) => {
      if (node.data.actionValue.type === IActionType.FilterData) {
        const chosenTableId = node.data.actionValue.settings?.tableId

        const table = tables?.find((tb) => tb.id === chosenTableId)

        const tableColumns = getTableColumnsWithRowId(table?.columns ?? []).filter(
          (c) =>
            ![
              ColumnTypes.Connection,
              ColumnTypes.RichText,
              ColumnTypes.RowID,
              ColumnTypes.Address,
              ColumnTypes.Slug,
              ColumnTypes.Array,
              ColumnTypes.QRCode,
              ColumnTypes.ImageArray,
              ColumnTypes.Barcode,
              ColumnTypes.AISEO,
              ColumnTypes.AITextProcessing,
            ].includes(c.type)
        )
        return {
          id: node.data.actionValue.id,
          label: node.data.actionValue.label || '',
          description: undefined,
          settings: node.data.actionValue.settings,
          actionType: node.data.actionValue.type,
          options: tableColumns.map((c) => ({
            id: node.data.actionValue.id,
            label: c.name,
            value: {
              tableId: chosenTableId,
              colId: c.id,
              colType: c.type,
              actionId: node.data.actionValue.id,
            },
            actionType: node.data.actionValue.type,
            description: undefined,
            optionsType: 'column',
          })),
        }
      }

      if (node.data.actionValue.type === IActionType.FormAddRow) {
        let chosenTableId = node.data.actionValue.settings?.tableId

        // handle submit button
        if (activeComponent?.compId === 'button') {
          const buttonId = activeComponent?.id
          const buttonComponent = activeLayerClient?.componentsMap.get(buttonId)
          if (buttonComponent && buttonComponent.parentId) {
            const formComponent = activeLayerClient?.componentsMap.get(buttonComponent.parentId)
            if (formComponent) {
              const formDataSource = formComponent.settings[ComponentSettingKey.FormDataSource]
              if (formDataSource) {
                chosenTableId = formDataSource
              }
            }
          }
        }

        const table = tables?.find((tb) => tb.id === chosenTableId)

        const tableColumns = getTableColumnsWithRowId(table?.columns ?? []).filter(
          (c) =>
            ![
              ColumnTypes.Connection,
              ColumnTypes.RichText,
              ColumnTypes.ImageArray,
              ColumnTypes.AISEO,
              ColumnTypes.AITextProcessing,
            ].includes(c.type)
        )

        const colOptions = tableColumns.map((c) => ({
          id: node.data.actionValue.id,
          label: c.name,
          value: {
            tableId: chosenTableId,
            colId: c.id,
            colType: c.type,
            actionId: node.data.actionValue.id,
          },
          actionType: node.data.actionValue.type,
          description: undefined,
          optionsType: 'column',
        }))

        return {
          id: node.data.actionValue.id,
          label: node.data.actionValue.label || 'New Item',
          description: undefined,
          settings: node.data.actionValue.settings,
          actionType: node.data.actionValue.type,
          options: colOptions,
        }
      }

      // Todo: return either settings or value
      return {
        id: node.data.actionValue.id,
        value: node.data.actionValue.settings.value,
        settings: node.data.actionValue.settings,
        label: node.data.actionValue.label || '',
        description: undefined,
        actionType: node.data.actionValue.type,
      }
    })
  }, [nodes, editedForm?.id, tables, activeComponent, activeLayerClient])

  // Get previous and next nodes for the currently edited node
  const currentNodeContext = React.useMemo(() => {
    if (!editedForm?.id) return null

    return WorkflowNodeManager.getNodeContext(nodes, edges, editedForm.id)
  }, [editedForm?.id, nodes, edges])

  // console.log({
  //   nodes,
  //   edges,
  //   getNodeOptions: getNodeOptions(),
  //   currentNodeContext: editedForm ? currentNodeContext : null,
  // })

  return (
    <div className='w-full h-full relative p-0 m-0'>
      {/* Auto Layout Controls */}
      <AutoLayoutControls />

      {/* Save Button */}
      <div className='fixed top-4 right-14 z-20 flex items-center gap-4'>
        <div className='flex h-full'>
          <Button
            noPadding
            squared
            disabled={!canUndo}
            onClick={handleUndo}
            variant={ButtonVariant.Ghost}
            className={cn(canUndo ? 'cursor-pointer' : 'cursor-not-allowed')}
          >
            <Undo16 width={18} height={18} />
          </Button>
          <Button
            noPadding
            squared
            disabled={!canRedo}
            onClick={handleRedo}
            variant={ButtonVariant.Ghost}
            className={cn(canRedo ? 'cursor-pointer' : 'cursor-not-allowed')}
          >
            <Redo16 width={18} height={18} />
          </Button>
        </div>

        <SaveButton
          isLoading={showSavingIndicator}
          disabled={showSavingIndicator}
          onClick={handleSaveWorkflow}
          label={showSavingIndicator ? 'Saving' : 'Save'}
        />
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={REACT_FLOW_CONFIG.fitViewOptions}
        minZoom={REACT_FLOW_CONFIG.minZoom}
        maxZoom={REACT_FLOW_CONFIG.maxZoom}
        onInit={() => {
          applyConditionalAwareLayout()
          fitView({ maxZoom: 1, minZoom: 0.7, padding: 0.8 })
        }}
      >
        <Background
          gap={REACT_FLOW_CONFIG.background.gap}
          size={REACT_FLOW_CONFIG.background.size}
          variant={BackgroundVariant.Dots}
        />
      </ReactFlow>

      <AddActionVerticalNodeModal
        show={isModalOpen}
        excludeActions={[...excludeActions, IActionType.PerformNextAction]}
        onHide={() => {
          setIsModalOpen(false)
          setSourceNodeId(null)
          setBranchInfo(null)
        }}
        onAddNode={handleAddNode}
        nodesCount={nodes.length}
        customActions={customPluginActions ?? []}
      />

      <WorkflowActionFormModal
        show={isFormModalOpen}
        onHide={() => {
          setIsFormModalOpen(false)
          setEditedForm(null)
          // Clear sourceNodeId if user cancels the form
          setSourceNodeId(null)
          setBranchInfo(null)
        }}
        onSubmit={handleOnSave}
        editedForm={editedForm}
        actionOptions={getNodeOptions()}
        setEditedForm={setEditedForm}
        nodeContext={currentNodeContext}
      />
    </div>
  )
}

export const AdvancedWorkflowEditor: React.FC<AdvancedWorkflowEditorProps> = (props) => {
  return (
    <ReactFlowProvider>
      <WorkflowProvider customPluginActions={props.customPluginActions}>
        <WorkflowEditorContent {...props} />
      </WorkflowProvider>
    </ReactFlowProvider>
  )
}
